'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

interface RoleFeatureBlockProps {
  role: string;
}

// ─── ROLE FEATURE BLOCKS ──────────────────────────────────────────────────────

const ROLE_FEATURE_BLOCKS: Record<string, {
  title: string;
  arabic: string;
  description: string;
  cta: string;
  href: string;
  secondaryCta: string;
  secondaryHref: string;
  tertiaryCta?: string;
  tertiaryHref?: string;
  accentColor: string;
  preview: string;
  badge: string;
}> = {
  'inner-seeker': {
    title: 'Inner Reality Lens',
    arabic: 'عَدَسَة الحَقِيقَة الدَّاخِلِيَّة',
    description: 'Eight layers of recognition — from Mata (encounter) to Uboor (crossing). The Leaf Sequence guides you from observation to understanding. Begin here.',
    cta: 'Begin in the IRL Lens →',
    href: '/inner-reality-lens',
    secondaryCta: 'Explore the Names →',
    secondaryHref: '/names-reference',
    tertiaryCta: 'Ayat in Creation →',
    tertiaryHref: '/ayat-in-creation',
    accentColor: '#8a8ab8',
    preview: 'Mata → Encounter → Examination → Recognition → Re-Assembly → Uboor',
    badge: 'Priority Path',
  },
  'community-caretaker': {
    title: 'Muslim Resource Guide + Justice',
    arabic: 'دَلِيل المُسْلِم وَالعَدَالَة',
    description: 'City-by-city resources, justice-involved support, and community networks. Find what your community needs — and what it\'s missing. Mizan-based frameworks for every caretaker.',
    cta: 'Find Resources in Your City →',
    href: '/muslim-resource-guide',
    secondaryCta: 'Justice Resources →',
    secondaryHref: '/muslim-resource-guide',
    tertiaryCta: 'Join the Caretakers →',
    tertiaryHref: '/community',
    accentColor: '#2d7a47',
    preview: 'Atlanta · Chicago · Houston · New York · Los Angeles · Justice-Involved',
    badge: 'Community Priority',
  },
  'academic': {
    title: 'Names Reference + Ayat Scholarship',
    arabic: 'مَرْجِع الأَسْمَاء وَعِلْم الآيَات',
    description: '99+ Names of Allah — the ontological vocabulary of the framework. Each Name a lens, each lens a methodology. Cross-linked to Ayat, Keys, and the SL Glossary.',
    cta: 'Explore the Names Reference →',
    href: '/names-reference',
    secondaryCta: 'Ayat in Creation →',
    secondaryHref: '/ayat-in-creation',
    tertiaryCta: 'SL Glossary →',
    tertiaryHref: '/sl-glossary',
    accentColor: '#d4a82a',
    preview: 'Al-Alim · Al-Khabir · Al-Hakim · Al-Zahir · Al-Batin · 90+ Names',
    badge: 'Scholarship First',
  },
  'activist': {
    title: 'Justice Resources',
    arabic: 'مَوَارِد العَدَالَة',
    description: 'Mizan-based frameworks for justice work. Redlining, environmental racism, community organizing — rooted in Al-Adl and Al-Haqq. The IRL Lens applied to your locality.',
    cta: 'Access Justice Resources →',
    href: '/muslim-resource-guide',
    secondaryCta: 'IRL Lens →',
    secondaryHref: '/inner-reality-lens',
    tertiaryCta: 'Names: Al-Adl →',
    tertiaryHref: '/names-reference',
    accentColor: '#2d8a87',
    preview: 'Al-Adl · Al-Haqq · Mizan · Islah · Amanah · Environmental Justice',
    badge: 'Justice Framework',
  },
  'local-resource': {
    title: 'List Your Resource',
    arabic: 'سَجِّل مَوْرِدَك',
    description: 'Make your services accessible to your community. The MRG connects local businesses, masajid, clinics, and schools to those who need them.',
    cta: 'List Your Resource →',
    href: '/muslim-resource-guide',
    secondaryCta: 'Join the Network →',
    secondaryHref: '/community',
    accentColor: '#5a5a8a',
    preview: 'Masajid · Clinics · Schools · Food Banks · Legal Aid',
    badge: 'Provider Path',
  },
};

// ─── ROLE SECTION ORDER ───────────────────────────────────────────────────────

// Returns ordered section keys for each role
// These map to the actual homepage sections rendered in RoleHomepage
export const ROLE_SECTION_ORDER: Record<string, string[]> = {
  'inner-seeker': [
    'hero',
    'role-feature',
    'philosophy',
    'framework-flow',
    'journey-map',
    'caretaker-assembly',
    'marquee',
    'bento',
    'vision',
    'resource-guide',
    'community-phases',
    'cta',
  ],
  'community-caretaker': [
    'hero',
    'role-feature',
    'resource-guide',
    'philosophy',
    'journey-map',
    'caretaker-assembly',
    'marquee',
    'framework-flow',
    'bento',
    'vision',
    'community-phases',
    'cta',
  ],
  'academic': [
    'hero',
    'role-feature',
    'framework-flow',
    'philosophy',
    'journey-map',
    'caretaker-assembly',
    'marquee',
    'bento',
    'resource-guide',
    'vision',
    'community-phases',
    'cta',
  ],
  'activist': [
    'hero',
    'role-feature',
    'resource-guide',
    'philosophy',
    'journey-map',
    'caretaker-assembly',
    'marquee',
    'framework-flow',
    'bento',
    'vision',
    'community-phases',
    'cta',
  ],
  'explorer': [
    'hero',
    'philosophy',
    'marquee',
    'journey-map',
    'framework-flow',
    'caretaker-assembly',
    'bento',
    'vision',
    'resource-guide',
    'community-phases',
    'cta',
  ],
};

// ─── ROLE FEATURE BLOCK COMPONENT ────────────────────────────────────────────

export function RoleFeatureBlock({ role }: RoleFeatureBlockProps) {
  const block = ROLE_FEATURE_BLOCKS[role];
  if (!block) return null;

  return (
    <div
      className="mx-4 md:mx-6 my-6 rounded-xl overflow-hidden"
      style={{
        background: 'rgba(6,13,18,0.97)',
        border: `1px solid ${block.accentColor}25`,
        borderLeft: `3px solid ${block.accentColor}`,
      }}
    >
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: `${block.accentColor}70` }}>
              ◈ Featured for your path
            </p>
            <h3 className="font-mono text-xl font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>
              {block.title}
            </h3>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${block.accentColor}60`, direction: 'rtl', marginTop: 2 }}>
              {block.arabic}
            </p>
          </div>
          <div className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 rounded" style={{ background: `${block.accentColor}10`, border: `1px solid ${block.accentColor}30`, color: block.accentColor }}>
            {block.badge}
          </div>
        </div>

        {/* Description */}
        <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 14, lineHeight: 1.7, marginBottom: 16, maxWidth: 600 }}>
          {block.description}
        </p>

        {/* Preview */}
        <div className="font-mono text-[10px] mb-5 px-3 py-2 rounded" style={{ background: `${block.accentColor}06`, border: `1px solid ${block.accentColor}12`, color: `${block.accentColor}60` }}>
          {block.preview}
        </div>

        {/* CTAs — hierarchical */}
        <div className="flex flex-wrap gap-3">
          {/* Primary CTA */}
          <Link
            href={block.href}
            className="font-mono text-xs uppercase tracking-widest px-5 py-2.5 transition-all duration-200"
            style={{ background: block.accentColor, color: '#060d12' }}
          >
            {block.cta}
          </Link>
          {/* Secondary CTA */}
          <Link
            href={block.secondaryHref}
            className="font-mono text-xs uppercase tracking-widest px-5 py-2.5 transition-all duration-200"
            style={{ background: 'transparent', border: `1px solid ${block.accentColor}45`, color: block.accentColor }}
          >
            {block.secondaryCta}
          </Link>
          {/* Tertiary CTA */}
          {block.tertiaryCta && block.tertiaryHref && (
            <Link
              href={block.tertiaryHref}
              className="font-mono text-xs uppercase tracking-widest px-5 py-2.5 transition-all duration-200"
              style={{ background: 'transparent', border: `1px solid ${block.accentColor}20`, color: `${block.accentColor}70` }}
            >
              {block.tertiaryCta}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ROLE HOMEPAGE WRAPPER ────────────────────────────────────────────────────

export default function RoleHomepage({ children }: { children: React.ReactNode }) {
  const [role, setRole] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const savedRole = localStorage.getItem('haal_role');
    setRole(savedRole);
  }, []);

  if (!mounted) return <>{children}</>;

  return (
    <>
      {children}
      {role && role !== 'explorer' && (
        <div
          className="fixed bottom-6 right-6 z-40 transition-all duration-500"
          style={{ opacity: mounted ? 1 : 0 }}
        >
          <Link
            href="/user-dashboard"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg font-mono text-[10px] uppercase tracking-widest transition-all duration-200"
            style={{ background: 'rgba(6,13,18,0.95)', border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c' }}
          >
            <span>◈</span>
            <span>My Journey</span>
          </Link>
        </div>
      )}
    </>
  );
}
